import { Link } from 'react-router-dom';
import {
  MetricCard,
  formatCount,
  formatUsd,
  metricInfo,
  metricTone,
  metricIcon,
} from '@kioskgaming/ui';
import type { DashboardV2Period, ShopDashboardV2OperationsData } from '../../services/api';

type Props = {
  period: DashboardV2Period;
  data: ShopDashboardV2OperationsData | null;
  isLoading: boolean;
};

const TX_TYPE_LABELS: Record<string, string> = {
  deposit_credits: 'Counter deposits',
  redeem_credits: 'Counter redemptions',
  process_withdrawal: 'Processed withdrawals',
};

const KPI_LAYOUT: Array<{
  key: string;
  format: (n: number) => string;
  point?: boolean;
  to?: string;
}> = [
  { key: 'openShifts', format: formatCount, point: true, to: '/shifts' },
  { key: 'discrepancyShifts', format: formatCount, to: '/shift-reports' },
  { key: 'discrepancyAmount', format: formatUsd },
  { key: 'noShowShifts', format: formatCount },
  { key: 'pendingRedemptionApprovals', format: formatCount, point: true, to: '/redemption-approvals' },
  { key: 'cashierTransactionsCount', format: formatCount },
  { key: 'cashierCashDelta', format: formatUsd },
  { key: 'cashDropsTotal', format: formatUsd },
  { key: 'cashDropsPendingCount', format: formatCount, point: true },
];

export default function OperationsTab({ data, isLoading }: Props) {
  const kpis = data?.kpis;
  const byType = data?.extras.cashierTxByType ?? {};

  return (
    <div className="space-y-6">
      <div className="grid grid-cols-1 gap-3 sm:grid-cols-2 xl:grid-cols-3">
        {KPI_LAYOUT.map(({ key, format, point, to }) => {
          const info = metricInfo(key);
          const cardNode = (
            <MetricCard
              key={key}
              label={info.label}
              tooltip={info.tooltip}
              value={format(kpis?.[key as keyof typeof kpis]?.value ?? 0)}
              kpi={point ? null : kpis?.[key as keyof typeof kpis] ?? null}
              goodWhen={info.goodWhen}
              tone={metricTone(key)}
              icon={metricIcon(key)}
              signAware={key === 'cashierCashDelta'}
              loading={isLoading}
            />
          );
          return to ? (
            <Link key={key} to={to} className="block transition hover:opacity-90">
              {cardNode}
            </Link>
          ) : (
            cardNode
          );
        })}
      </div>

      <div className="rounded-2xl border border-gray-200 bg-white p-5">
        <h3 className="text-sm font-semibold text-gray-800">Counter activity by type</h3>
        <p className="mt-0.5 text-xs text-gray-500">Completed Cashier transactions in the selected period.</p>
        {Object.keys(byType).length === 0 ? (
          <p className="mt-4 text-sm text-gray-500">No counter activity in this period.</p>
        ) : (
          <ul className="mt-4 divide-y divide-gray-100">
            {Object.entries(byType).map(([type, stats]) => (
              <li key={type} className="flex items-center justify-between py-3 text-sm">
                <span className="font-medium text-gray-800">{TX_TYPE_LABELS[type] ?? type}</span>
                <span className="tabular-nums text-gray-600">
                  {formatCount(stats.count)} · {formatUsd(stats.amount)}
                </span>
              </li>
            ))}
          </ul>
        )}
      </div>
    </div>
  );
}
